import React, { useState, lazy } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Users, ShieldCheck, MapPin, AlertTriangle, Clock, MessageSquare, CheckCircle, Navigation as NavigationIcon, ZoomIn, ZoomOut, Maximize2, Layers } from 'lucide-react';
import { motion } from 'framer-motion';
const liveReports = [{
  id: 1,
  type: 'student',
  reporter: 'Chioma A.',
  category: 'Safety Concern',
  subject: 'Broken gate lock at Emerald Heights',
  location: 'Unilag, Akoka',
  priority: 'high',
  status: 'open',
  time: '5 mins ago',
  coordinates: {
    lat: 6.5158,
    lng: 3.3895
  }
}, {
  id: 2,
  type: 'landlord',
  reporter: 'Mr. Okafor',
  category: 'Tenant Issue',
  subject: 'Noise complaint from neighboring rooms',
  location: 'Sunshine Lodge, Yaba',
  priority: 'medium',
  status: 'investigating',
  time: '23 mins ago',
  coordinates: {
    lat: 6.5027,
    lng: 3.3724
  }
}, {
  id: 3,
  type: 'student',
  reporter: 'David O.',
  category: 'Maintenance',
  subject: 'Water supply interrupted for 2 days',
  location: 'The Haven, Bariga',
  priority: 'high',
  status: 'open',
  time: '1 hour ago',
  coordinates: {
    lat: 6.5355,
    lng: 3.3889
  }
}, {
  id: 4,
  type: 'student',
  reporter: 'Sarah K.',
  category: 'Security',
  subject: 'Suspicious activity near hostel entrance',
  location: 'Student Village, LUTH',
  priority: 'urgent',
  status: 'open',
  time: '2 hours ago',
  coordinates: {
    lat: 6.4969,
    lng: 3.3774
  }
}];
const studentLocations = [{
  id: 1,
  name: 'Emerald Heights',
  students: 45,
  status: 'safe',
  lat: 6.5158,
  lng: 3.3895
}, {
  id: 2,
  name: 'Sunshine Lodge',
  students: 32,
  status: 'safe',
  lat: 6.5027,
  lng: 3.3724
}, {
  id: 3,
  name: 'The Haven',
  students: 28,
  status: 'warning',
  lat: 6.5355,
  lng: 3.3889
}, {
  id: 4,
  name: 'Student Village',
  students: 51,
  status: 'alert',
  lat: 6.4969,
  lng: 3.3774
}, {
  id: 5,
  name: 'Campus View',
  students: 38,
  status: 'safe',
  lat: 6.5244,
  lng: 3.3812
}];
export function UniversityDashboard() {
  const [selectedLocation, setSelectedLocation] = useState<(typeof studentLocations)[0] | null>(null);
  const [mapView, setMapView] = useState<'satellite' | 'roadmap'>('roadmap');
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved':
        return 'success';
      case 'investigating':
        return 'warning';
      case 'open':
        return 'danger';
      default:
        return 'neutral';
    }
  };
  const getLocationStatusColor = (status: string) => {
    switch (status) {
      case 'safe':
        return 'bg-emerald-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'alert':
        return 'bg-red-500';
      default:
        return 'bg-slate-500';
    }
  };
  // Google Maps embed URL with markers
  const mapUrl = `https://www.google.com/maps/embed/v1/view?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&center=6.5158,3.3895&zoom=13&maptype=${mapView}`;
  return <DashboardLayout userType="university" data-id="element-1262">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-id="element-1263">
        <Card data-id="element-1264">
          <CardContent className="p-6" data-id="element-1265">
            <div className="text-sm text-slate-500 mb-1" data-id="element-1266">
              Total Students Tracked
            </div>
            <div className="text-3xl font-bold text-brand-dark" data-id="element-1267">12,450</div>
            <div className="text-xs text-emerald-600 font-medium mt-1" data-id="element-1268">
              95% Off-campus coverage
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-1269">
          <CardContent className="p-6" data-id="element-1270">
            <div className="text-sm text-slate-500 mb-1" data-id="element-1271">
              Verified Properties
            </div>
            <div className="text-3xl font-bold text-brand-dark" data-id="element-1272">850</div>
            <div className="text-xs text-emerald-600 font-medium mt-1" data-id="element-1273">
              +24 this month
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-1274">
          <CardContent className="p-6" data-id="element-1275">
            <div className="text-sm text-slate-500 mb-1" data-id="element-1276">
              Active Ambassadors
            </div>
            <div className="text-3xl font-bold text-brand-dark" data-id="element-1277">120</div>
            <div className="text-xs text-slate-400 mt-1" data-id="element-1278">Simba Fleet</div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-100" data-id="element-1279">
          <CardContent className="p-6" data-id="element-1280">
            <div className="text-sm text-red-600 mb-1" data-id="element-1281">Open Reports</div>
            <div className="text-3xl font-bold text-red-700" data-id="element-1282">8</div>
            <div className="text-xs text-red-600 font-medium mt-1" data-id="element-1283">
              Requires attention
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8" data-id="element-1284">
        {/* Google Maps */}
        <Card className="lg:col-span-2" data-id="element-1285">
          <CardHeader className="flex flex-row items-center justify-between" data-id="element-1286">
            <CardTitle data-id="element-1287">Live Safety Map - Lagos Area</CardTitle>
            <div className="flex gap-2" data-id="element-1288">
              <button onClick={() => setMapView(mapView === 'roadmap' ? 'satellite' : 'roadmap')} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" title="Toggle map view" data-id="element-1289">
                <Layers className="w-4 h-4" data-id="element-1290" />
              </button>
              <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-1291">
                <Maximize2 className="w-4 h-4" data-id="element-1292" />
              </button>
            </div>
          </CardHeader>
          <CardContent className="h-96 rounded-b-xl relative overflow-hidden p-0" data-id="element-1293">
            {/* Google Maps Embed */}
            <iframe width="100%" height="100%" style={{
            border: 0
          }} loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" src={mapUrl} className="rounded-b-xl" data-id="element-1294"></iframe>

            {/* Legend Overlay */}
            <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-lg z-10" data-id="element-1295">
              <div className="text-xs font-bold text-brand-dark mb-2" data-id="element-1296">
                Status Legend
              </div>
              <div className="space-y-1" data-id="element-1297">
                <div className="flex items-center gap-2 text-xs" data-id="element-1298">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" data-id="element-1299"></div>
                  <span data-id="element-1300">
                    Safe (
                    {studentLocations.filter(l => l.status === 'safe').length}
                    )
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs" data-id="element-1301">
                  <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-1302"></div>
                  <span data-id="element-1303">
                    Warning (
                    {studentLocations.filter(l => l.status === 'warning').length}
                    )
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs" data-id="element-1304">
                  <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-1305"></div>
                  <span data-id="element-1306">
                    Alert (
                    {studentLocations.filter(l => l.status === 'alert').length}
                    )
                  </span>
                </div>
              </div>
            </div>

            {/* Location Info Overlay */}
            <div className="absolute top-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-lg z-10" data-id="element-1307">
              <div className="text-xs font-bold text-brand-dark mb-1" data-id="element-1308">
                Active Monitoring
              </div>
              <div className="text-xs text-slate-600" data-id="element-1309">
                Tracking{' '}
                {studentLocations.reduce((acc, loc) => acc + loc.students, 0)}{' '}
                students across {studentLocations.length} verified locations
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Location Details */}
        <Card data-id="element-1310">
          <CardHeader data-id="element-1311">
            <CardTitle data-id="element-1312">Location Overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3" data-id="element-1313">
            {studentLocations.map(location => <div key={location.id} className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer" onClick={() => setSelectedLocation(location)} data-id="element-1314">
                <div className="flex justify-between items-start mb-2" data-id="element-1315">
                  <div className="font-bold text-brand-dark text-sm" data-id="element-1316">
                    {location.name}
                  </div>
                  <div className={`w-3 h-3 rounded-full ${getLocationStatusColor(location.status)}`} data-id="element-1317"></div>
                </div>
                <div className="flex justify-between items-center text-xs" data-id="element-1318">
                  <span className="text-slate-600" data-id="element-1319">
                    {location.students} students
                  </span>
                  <span className="text-slate-500 capitalize" data-id="element-1320">
                    {location.status}
                  </span>
                </div>
              </div>)}
          </CardContent>
        </Card>
      </div>

      {/* Live Reports Section */}
      <Card data-id="element-1321">
        <CardHeader className="flex flex-row items-center justify-between" data-id="element-1322">
          <div data-id="element-1323">
            <CardTitle data-id="element-1324">Live Reports & Cases</CardTitle>
            <p className="text-sm text-slate-500 mt-1" data-id="element-1325">
              Real-time reports from students and landlords
            </p>
          </div>
          <div className="flex gap-2" data-id="element-1326">
            <Button variant="outline" size="sm" data-id="element-1327">
              Filter
            </Button>
            <Button size="sm" data-id="element-1328">New Report</Button>
          </div>
        </CardHeader>
        <div className="overflow-x-auto" data-id="element-1329">
          <table className="w-full text-sm text-left" data-id="element-1330">
            <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-1331">
              <tr data-id="element-1332">
                <th className="px-6 py-4" data-id="element-1333">Reporter</th>
                <th className="px-6 py-4" data-id="element-1334">Category</th>
                <th className="px-6 py-4" data-id="element-1335">Subject</th>
                <th className="px-6 py-4" data-id="element-1336">Location</th>
                <th className="px-6 py-4" data-id="element-1337">Priority</th>
                <th className="px-6 py-4" data-id="element-1338">Status</th>
                <th className="px-6 py-4" data-id="element-1339">Time</th>
                <th className="px-6 py-4" data-id="element-1340">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1341">
              {liveReports.map(report => <tr key={report.id} className="hover:bg-slate-50" data-id="element-1342">
                  <td className="px-6 py-4" data-id="element-1343">
                    <div className="flex items-center gap-2" data-id="element-1344">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${report.type === 'student' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`} data-id="element-1345">
                        {report.type === 'student' ? 'S' : 'L'}
                      </div>
                      <div data-id="element-1346">
                        <div className="font-medium text-brand-dark" data-id="element-1347">
                          {report.reporter}
                        </div>
                        <div className="text-xs text-slate-500 capitalize" data-id="element-1348">
                          {report.type}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-1349">
                    <span className="text-slate-700 font-medium" data-id="element-1350">
                      {report.category}
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-1351">
                    <div className="max-w-xs" data-id="element-1352">
                      <p className="text-slate-900 font-medium truncate" data-id="element-1353">
                        {report.subject}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-1354">
                    <div className="flex items-center gap-1 text-slate-600" data-id="element-1355">
                      <MapPin className="w-3 h-3" data-id="element-1356" />
                      <span className="text-xs" data-id="element-1357">{report.location}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-1358">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${getPriorityColor(report.priority)}`} data-id="element-1359">
                      {report.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-1360">
                    <Badge variant={getStatusColor(report.status) as any} className="capitalize" data-id="element-1361">
                      {report.status}
                    </Badge>
                  </td>
                  <td className="px-6 py-4" data-id="element-1362">
                    <div className="flex items-center gap-1 text-slate-500 text-xs" data-id="element-1363">
                      <Clock className="w-3 h-3" data-id="element-1364" />
                      {report.time}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-1365">
                    <div className="flex gap-2" data-id="element-1366">
                      <Button variant="ghost" size="sm" className="text-brand-primary hover:text-brand-light hover:bg-brand-primary/10" data-id="element-1367">
                        <MessageSquare className="w-4 h-4" data-id="element-1368" />
                      </Button>
                      {report.status !== 'resolved' && <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" data-id="element-1369">
                          <CheckCircle className="w-4 h-4" data-id="element-1370" />
                        </Button>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
    </DashboardLayout>;
}